"use client"

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react"

/* GTM dataLayer tracking for marketing landing pages (ASH-1854).

   The main site's helpers in utils/gtm.ts use a different vocabulary
   ("main menu" vs "menu_click"), so landing pages get their own module rather
   than bending those. Every event carries company_name and project_type, which
   come from the page once and are shared through context - blocks receive only
   their own block data, never the page's (see CLAUDE.md). */

type WindowWithDataLayer = Window & { dataLayer?: Record<string, unknown>[] }

/** Element names are fixed per component slot by the spec. */
export const LANDING_ELEMENTS = {
  header: "תפריט ניווט האדר",
  hero: "hero banner",
  powerNumbers: "מספרי כח",
  ashtromCompanies: "חברות אשטרום_הזרועות",
  benefits: "יתרונות",
  gallery: "גלריה",
  additionalProjects: "פרויקטים נוספים",
  leadForm: "טופס צור קשר",
  /* The About/Opening family (aboutOpening1..6) is a distinct set of CMS block
     types that the spec does not name. Their media and CTA interactions were
     reported as "hero banner", which silently merged them into the hero's
     figures, so they get their own slot. Confirm the wording with the analytics
     owner before this is read as final. */
  aboutOpening: "פתיח אודות",
  aboutClosing: "סגיר אודות",
  faq: "שאלות תשובות",
  reviews: "עדויות",
  interactiveMap: "מפה אינטראקטיבית",
  stickyButtons: "כפתורים סטיקים",
  footer: "footer",
  explanation: "הסבר",
} as const

export type LandingElement = (typeof LANDING_ELEMENTS)[keyof typeof LANDING_ELEMENTS]

type PageContext = {
  companyName: string
  projectType: string
  language: string
}

const LandingAnalyticsContext = createContext<PageContext>({
  companyName: "",
  projectType: "",
  language: "",
})

/* The spec opens with "All parameters and values must be lowercase". Hebrew is
   caseless, so in practice this normalises the Latin values. */
const lower = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : value)

function push(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return
  const w = window as WindowWithDataLayer
  // GTM may be disabled per page (scriptGtmEnabled), so there is no dataLayer
  if (!Array.isArray(w.dataLayer)) return

  const clean: Record<string, unknown> = {}
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return
    clean[key.toLowerCase()] = lower(value)
  })
  w.dataLayer.push(clean)
}

export function LandingAnalyticsProvider({
  companyName,
  projectType,
  language,
  children,
}: PageContext & { children: ReactNode }) {
  const value = useMemo(
    () => ({ companyName, projectType, language }),
    [companyName, projectType, language],
  )

  // "Page Properties" - one global event per page load, before any interaction
  useEffect(() => {
    push({
      event: "page properties",
      company_name: companyName,
      project_type: projectType,
      language,
    })
  }, [companyName, projectType, language])

  return (
    <LandingAnalyticsContext.Provider value={value}>{children}</LandingAnalyticsContext.Provider>
  )
}

/** Emitters for every event in the spec. Blocks call these; the shared
    company_name / project_type are filled in from context. */
export function useLandingAnalytics() {
  const { companyName, projectType } = useContext(LandingAnalyticsContext)

  return useMemo(() => {
    const base = { company_name: companyName, project_type: projectType }
    const emit = (event: string, params: Record<string, unknown>) =>
      push({ event, ...params, ...base })

    return {
      /** Header nav links and icons. */
      menuClick: (clickText: string) =>
        emit("menu_click", { click_text: clickText, element_name: LANDING_ELEMENTS.header }),

      /** Any call-to-action button, element_name identifies the block. */
      ctaClick: (clickText: string, elementName: LandingElement) =>
        emit("cta_click", { click_text: clickText, element_name: elementName }),

      /** Video / gallery interaction, e.g. clickText "pause". */
      mediaClick: (clickText: string, elementName: LandingElement) =>
        emit("media_click", { click_text: clickText, element_name: elementName }),

      /** Project card. subCompanyName is set for cards in the expanded gallery. */
      projectClick: (clickText: string, subCompanyName?: string) =>
        emit("project_click", {
          click_text: clickText,
          sub_company_name: subCompanyName,
          element_name: LANDING_ELEMENTS.additionalProjects,
        }),

      formSuccess: (clickText: string) =>
        emit("join_form_success", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.leadForm,
        }),

      formError: (clickText: string, errorName: string) =>
        emit("error_join_form", {
          click_text: clickText,
          error_name: errorName,
          element_name: LANDING_ELEMENTS.leadForm,
        }),

      /** Question title clicked to open it. */
      faqClick: (clickText: string) =>
        emit("faq_click", { click_text: clickText, element_name: LANDING_ELEMENTS.faq }),

      /** Carousel arrows or dots, e.g. clickText "next_slide". */
      carouselNavigation: (clickText: string, elementName: LandingElement) =>
        emit("carousel_navigation", { click_text: clickText, element_name: elementName }),

      /** Link or button inside a review, subClickText is the reviewer name. */
      reviewButtonClick: (clickText: string, subClickText?: string) =>
        emit("button_click", {
          click_text: clickText,
          sub_click_text: subClickText,
          element_name: LANDING_ELEMENTS.reviews,
        }),

      /** Switching between map and street view. */
      mapToggle: (clickText: string) =>
        emit("map_toggle", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.interactiveMap,
        }),

      /** Main action button in the floating bar. */
      stickyButtonClick: (clickText: string) =>
        emit("sticky_button_click", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.stickyButtons,
        }),

      /** Phone dial button or a flyout sub-button. */
      c2cClick: (clickText: string) =>
        emit("c2c_click", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.stickyButtons,
        }),

      socialClick: (clickText: string) =>
        emit("social_click", { click_text: clickText, element_name: LANDING_ELEMENTS.footer }),

      footerLinkClick: (clickText: string) =>
        emit("footer_link_click", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.footer,
        }),

      /** Link inside the Explanation block. */
      textLinkClick: (clickText: string) =>
        emit("text_link_click", {
          click_text: clickText,
          element_name: LANDING_ELEMENTS.explanation,
        }),
    }
  }, [companyName, projectType])
}
